import React, { useState } from "react";
import { api } from "@/lib/api";
import { PrimaryButton, GhostButton, Card, Field, Select, Textarea, Input } from "@/components/Primitives";
import { toast } from "sonner";

const STATUSES = ["Decision Needed", "Bid", "No-Bid", "Hold / Awaiting Info"];

const BID_REASONS = [
  "High product fit", "Strong buyer fit", "Deadline feasible", "Supplier ready",
  "Samples feasible", "Required standards achievable", "Attractive margin",
  "Strategic market entry", "Prior experience", "Other",
];
const NO_BID_REASONS = [
  "Deadline too close", "Eligibility risk", "Samples not feasible", "Testing not feasible",
  "Margin too low", "Local partner required", "Certification gap", "Specs mismatch",
  "Low win probability", "Production capacity risk", "Strategic mismatch", "Other",
];

export default function BidDecisionModal({ open, opportunity, onClose, onSaved }) {
  const [status, setStatus] = useState(opportunity?.bid_decision_status || "Decision Needed");
  const [rationale, setRationale] = useState(opportunity?.bid_decision_rationale || "");
  const [reasons, setReasons] = useState(opportunity?.bid_decision_reasons || []);
  const [nextAction, setNextAction] = useState(opportunity?.bid_decision_next_action || "");
  const [nextDue, setNextDue] = useState(opportunity?.bid_decision_next_action_due_date || "");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const reasonSet = status === "Bid" ? BID_REASONS : status === "No-Bid" ? NO_BID_REASONS : [];
  const isFinal = status === "Bid" || status === "No-Bid";
  const isHold = status === "Hold / Awaiting Info";

  const toggleReason = (r) =>
    setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  // Client-side validation mirrors backend (backend is the source of truth).
  const errors = [];
  if (!STATUSES.includes(status)) errors.push("Select a decision status.");
  if (isFinal && !rationale.trim()) errors.push("Rationale is required for a final Bid or No-Bid decision.");
  if (status === "No-Bid" && reasons.filter((r) => NO_BID_REASONS.includes(r)).length === 0)
    errors.push("Select at least one reason for a No-Bid decision.");

  const save = async () => {
    if (errors.length) { toast.error(errors[0]); return; }
    setBusy(true);
    try {
      const cleanReasons = reasons.filter((r) => reasonSet.includes(r));
      const r = await api.post(`/opportunities/${opportunity.id}/bid-decision`, {
        bid_decision_status: status,
        bid_decision_rationale: rationale,
        bid_decision_reasons: cleanReasons,
        bid_decision_next_action: isHold ? nextAction : "",
        bid_decision_next_action_due_date: isHold ? nextDue : "",
      });
      toast.success(`Decision recorded: ${r.data.opportunity.bid_decision_status}`);
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save decision");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8" data-testid="bid-decision-modal">
      <Card className="max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Strategy Gate</div>
            <h2 className="font-heading text-xl tracking-tighter">Bid / No-Bid Decision</h2>
            <p className="text-xs text-zinc-500 mt-1 truncate max-w-[420px]">{opportunity?.opportunity_name}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="bid-decision-close">×</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Decision Status">
            <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="bid-decision-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
        </div>

        {reasonSet.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
              {status === "No-Bid" ? "No-Bid Reasons" : "Bid Reasons"}{status === "No-Bid" && <span className="text-[#FF3B30]"> *</span>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="bid-decision-reasons">
              {reasonSet.map((r) => (
                <label key={r} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer hover:text-white">
                  <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggleReason(r)} data-testid={`bid-reason-${r.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                  {r}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <Field label={`Rationale${isFinal ? " *" : ""}`}>
            <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)}
              placeholder={isFinal ? "Required — explain the decision" : "Optional context"}
              data-testid="bid-decision-rationale" />
          </Field>
        </div>

        {isHold && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <Field label="Next Action">
              <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="e.g. Request clarification from buyer" data-testid="bid-decision-next-action" />
            </Field>
            <Field label="Next Action Due">
              <Input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} data-testid="bid-decision-next-due" />
            </Field>
          </div>
        )}

        {errors.length > 0 && (
          <div className="mt-3 text-[11px] text-[#FFB300]" data-testid="bid-decision-errors">{errors[0]}</div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <GhostButton onClick={onClose} data-testid="bid-decision-cancel">Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={busy || errors.length > 0} data-testid="bid-decision-save">{busy ? "Saving…" : "Save Decision"}</PrimaryButton>
        </div>
      </Card>
    </div>
  );
}
